import Button from "@/lib/Button";
import { useState } from "react";
import EditEmails from "@/lib/sendEmail/EditEmails";
import AttachFiles from "@/lib/AttachFiles";
import useToPage from "@/lib/useToPage";
import { useFetchRfq } from "@/lib/queries/hooks";
import Message from "@/lib/sendEmail/Message";
import $api from "@/lib/queries/openapi";
import { useParams } from "react-router";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import Print from "@/lib/Print";
import Download from "@/lib/Download";
import useGetTeamSettings from "@/lib/queries/useGetTeamSettings";

export default function Send() {
  const { toDone } = useToPage();
  const [files, setFiles] = useState<File[]>([]);
  const rfq = useFetchRfq();
  const quoteRevision = useGetCurrentQuoteRevision();
  const canEditQuoteRevision = quoteRevision?.can_edit;
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const teamSettings = useGetTeamSettings(teamSlug);

  const { mutate: send, isPending } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/quote_revisions/{id}/send",
    {
      onError: ({ detail }) => {
        toast.error(detail, {
          className: "whitespace-pre-wrap",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
        });
        toDone();
      },
    },
  );

  if (!rfq || !quoteRevision) return null;

  const quotePdfUrl = `/api/teams/${teamSlug}/quote_revisions/${quoteRevision.id}/quote.pdf`;

  return (
    <div className="w-full h-full flex">
      <div className="h-full basis-2/3 flex flex-col p-2 gap-1">
        <div className="flex gap-2 self-end">
          <Print fileUrl={quotePdfUrl} />
          <Download fileUrl={quotePdfUrl} />
        </div>
        {rfq && (
          <iframe
            src={`${quotePdfUrl}#toolbar=0&navpanes=0&view=FitH&zoom=page-fit`}
            className="h-full w-full"
          />
        )}
      </div>
      <div className="bg-white h-full basis-1/3 flex flex-col justify-between">
        <div className="flex flex-col gap-6 p-6">
          <EditEmails
            quoteRevisionId={quoteRevision.id}
            disabled={!canEditQuoteRevision}
          />
          <Message
            quoteRevisionId={quoteRevision.id}
            disabled={!canEditQuoteRevision}
            defaultEmailBody={teamSettings?.default_email_body ?? ""}
          />
          <AttachFiles
            files={files}
            setFiles={setFiles}
            disabled={!canEditQuoteRevision}
          />
        </div>
        {canEditQuoteRevision && (
          <div className="flex justify-end p-4">
            <Button
              primary
              loading={isPending}
              onClick={() => {
                send({
                  params: {
                    path: { team_slug: teamSlug!, id: quoteRevision.id },
                  },
                  body: {
                    attached_files: files as unknown[] as string[],
                  },
                  bodySerializer: (body) => {
                    // `!body.attached_files` is here just to satisfy typescript,
                    // but files.length check is necessary and returning undefined also to make it work without files
                    if (files.length === 0 || !body.attached_files) {
                      return undefined;
                    }

                    // file upload is unfortunately not properly supported by openapi-typescript: https://github.com/openapi-ts/openapi-typescript/issues/1214
                    // luckily, there's a workaround https://github.com/openapi-ts/openapi-typescript/issues/1214#issuecomment-1957965890
                    const formData = new FormData();
                    for (const file of body.attached_files) {
                      formData.append("attached_files", file);
                    }
                    return formData;
                  },
                });
              }}
            >
              Send Quote
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
